import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import {
    ensureSpeechCalibrationDailyQueue,
    getSpeechCalibrationCapabilities,
    getSpeechCalibrationDailyQueue,
    type SpeechCalibrationCapabilities,
    type SpeechCalibrationDailyQueue,
    type SpeechCalibrationQueueItem,
} from '../admin/adminSpeechCalibrationApi';
import {
    addUtcDays,
    formatDateTime,
    formatDuration,
    todayUtc,
} from './speechCalibrationConstants';
import {
    MetricCard,
    StatusChip,
} from './speechCalibrationUi';

function QueueActions({
    item,
    onOpen,
}: {
    item: SpeechCalibrationQueueItem;
    onOpen: (assignmentId: string) => void;
}) {
    const complete = item.status.toLowerCase().includes('compar');
    return (
        <Button
            size="small"
            variant={complete ? 'outlined' : 'contained'}
            onClick={() => onOpen(item.assignmentId)}
        >
            {complete ? 'View' : item.status.toLowerCase().includes('blind') ? 'Compare' : 'Review'}
        </Button>
    );
}

function MobileQueueCard({
    item,
    onOpen,
}: {
    item: SpeechCalibrationQueueItem;
    onOpen: (assignmentId: string) => void;
}) {
    return (
        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Box>
                    <Typography sx={{ color: '#0f172a', fontWeight: 850 }}>
                        {item.examTaskType.replace(/([a-z])([A-Z])/g, '$1 $2')}
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                        {item.examName} · Question {item.questionNo ?? '—'} · {formatDuration(item.durationSeconds)}
                    </Typography>
                </Box>
                <StatusChip status={item.status} />
            </Stack>
            {item.selectionReasons.length > 0 ? (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                    {item.selectionReasons.map((reason) => (
                        <Chip key={reason} size="small" label={reason.replaceAll('_', ' ')} variant="outlined" />
                    ))}
                </Stack>
            ) : null}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                    {item.completedAtUtc ? formatDateTime(item.completedAtUtc) : 'Waiting for your judgment'}
                </Typography>
                <QueueActions item={item} onOpen={onOpen} />
            </Stack>
        </Paper>
    );
}

export function SpeechCalibrationQueuePage() {
    const navigate = useNavigate();
    const currentDay = todayUtc();
    const [selectedDay, setSelectedDay] = useState(currentDay);
    const [status, setStatus] = useState('');
    const [capabilities, setCapabilities] = useState<SpeechCalibrationCapabilities | null>(null);
    const [queue, setQueue] = useState<SpeechCalibrationDailyQueue | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEnsuring, setIsEnsuring] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [nextCapabilities, nextQueue] = await Promise.all([
                getSpeechCalibrationCapabilities(),
                getSpeechCalibrationDailyQueue(selectedDay, { status, pageSize: 100 }),
            ]);
            setCapabilities(nextCapabilities);
            setQueue(nextQueue);
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Unable to load the calibration queue');
        } finally {
            setIsLoading(false);
        }
    }, [selectedDay, status]);

    useEffect(() => {
        void load();
    }, [load]);

    const ensureQueue = async () => {
        setIsEnsuring(true);
        setError(null);
        setNotice(null);
        try {
            const nextQueue = await ensureSpeechCalibrationDailyQueue(selectedDay);
            setQueue(nextQueue);
            setNotice(
                nextQueue.createdCount
                    ? `${nextQueue.createdCount} new blind-review assignments were added.`
                    : 'Your daily queue is already prepared.',
            );
        } catch (ensureError) {
            setError(ensureError instanceof Error ? ensureError.message : 'Unable to prepare the daily queue');
        } finally {
            setIsEnsuring(false);
        }
    };

    const summary = queue?.summary;
    const completed = (summary?.blindRated ?? 0) + (summary?.compared ?? 0);
    const target = summary?.target ?? capabilities?.dailyTarget ?? 100;
    const completionPercent = target > 0 ? Math.min(100, (completed / target) * 100) : 0;
    const statusOptions = useMemo(
        () => [...new Set(['', ...(capabilities?.statuses ?? [])])],
        [capabilities?.statuses],
    );

    return (
        <Box sx={{ p: { xs: 1.25, md: 2.5 }, minWidth: 0 }}>
            <Title title="Speech Calibration" />
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 900 }}>
                        Daily speech calibration
                    </Typography>
                    <Typography sx={{ mt: 0.5, color: '#64748b' }}>
                        Listen independently, rate the four delivery traits, then compare with the experimental model.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={() => void load()}
                        disabled={isLoading}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={isEnsuring ? <CircularProgress size={16} color="inherit" /> : <PlaylistAddCheckIcon />}
                        onClick={() => void ensureQueue()}
                        disabled={isEnsuring}
                    >
                        Prepare my {target}
                    </Button>
                </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2.5 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ArrowBackIosNewIcon />}
                            onClick={() => setSelectedDay(addUtcDays(selectedDay, -1))}
                        >
                            Previous
                        </Button>
                        <Button
                            size="small"
                            variant={selectedDay === currentDay ? 'contained' : 'outlined'}
                            onClick={() => setSelectedDay(currentDay)}
                        >
                            Today
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={<ArrowForwardIosIcon />}
                            disabled={selectedDay >= currentDay}
                            onClick={() => setSelectedDay(addUtcDays(selectedDay, 1))}
                        >
                            Next
                        </Button>
                        <Typography sx={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 800 }}>
                            {selectedDay} UTC
                        </Typography>
                    </Stack>
                    <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel id="speech-calibration-status-label">Status</InputLabel>
                        <Select
                            labelId="speech-calibration-status-label"
                            label="Status"
                            value={status}
                            onChange={(event) => setStatus(String(event.target.value))}
                        >
                            {statusOptions.map((option) => (
                                <MenuItem key={option || 'all'} value={option}>
                                    {option ? option.replaceAll('_', ' ') : 'All statuses'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 2, mb: 0.75 }}>
                    <Typography sx={{ fontWeight: 800 }}>{completed} of {target} judgments</Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>{Math.round(completionPercent)}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={completionPercent} sx={{ height: 8, borderRadius: 999 }} />
            </Paper>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {notice ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert> : null}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
                    gap: 1.25,
                    mb: 2,
                }}
            >
                <MetricCard label="Assigned" value={summary?.total ?? 0} />
                <MetricCard label="Pending" value={summary?.pending ?? 0} />
                <MetricCard label="Blind rated" value={summary?.blindRated ?? 0} />
                <MetricCard label="Compared" value={summary?.compared ?? 0} />
                <MetricCard label="Adjudication" value={summary?.needsAdjudication ?? 0} />
                <MetricCard label="Unusable" value={summary?.unusable ?? 0} />
            </Box>

            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ color: '#0f172a', fontWeight: 850 }}>Your assignments</Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                        The same recording is independently assigned to two raters. Other raters' judgments remain hidden.
                    </Typography>
                </Box>

                {isLoading && !queue ? (
                    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 220 }}><CircularProgress /></Box>
                ) : (
                    <>
                        <Stack spacing={1} sx={{ display: { xs: 'flex', md: 'none' }, p: 1.25 }}>
                            {queue?.items.map((item) => (
                                <MobileQueueCard
                                    key={item.assignmentId}
                                    item={item}
                                    onOpen={(id) => navigate(`/speech-calibration/review/${id}`)}
                                />
                            ))}
                        </Stack>
                        <TableContainer sx={{ display: { xs: 'none', md: 'block' }, maxHeight: '58vh' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 850 }}>Task</TableCell>
                                        <TableCell sx={{ fontWeight: 850 }}>Question</TableCell>
                                        <TableCell sx={{ fontWeight: 850 }}>Duration</TableCell>
                                        <TableCell sx={{ fontWeight: 850 }}>Selection</TableCell>
                                        <TableCell sx={{ fontWeight: 850 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 850 }}>Completed</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 850 }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {queue?.items.map((item) => (
                                        <TableRow key={item.assignmentId} hover>
                                            <TableCell>
                                                <Typography sx={{ fontWeight: 750 }}>{item.examTaskType}</Typography>
                                                <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>{item.examName}</Typography>
                                            </TableCell>
                                            <TableCell>{item.questionNo ?? '—'}</TableCell>
                                            <TableCell>{formatDuration(item.durationSeconds)}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                                    {item.selectionReasons.map((reason) => (
                                                        <Chip key={reason} size="small" label={reason.replaceAll('_', ' ')} variant="outlined" />
                                                    ))}
                                                </Stack>
                                            </TableCell>
                                            <TableCell><StatusChip status={item.status} /></TableCell>
                                            <TableCell>{formatDateTime(item.completedAtUtc)}</TableCell>
                                            <TableCell align="right">
                                                <QueueActions
                                                    item={item}
                                                    onOpen={(id) => navigate(`/speech-calibration/review/${id}`)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
                {!isLoading && (queue?.items.length ?? 0) === 0 ? (
                    <Typography sx={{ py: 5, textAlign: 'center', color: '#64748b' }}>
                        No assignments match this day and status.
                    </Typography>
                ) : null}
            </Paper>
        </Box>
    );
}
